import { useHookstate } from "@hookstate/core";
import { GCModal } from "../modals/GCModal";
import { FUNNEL_TYPES } from "./utils/contants";
import { rootComponentsStore } from "../../hookstate/rootComponentsStore";
import {
  FunnelConfigTypes,
  FunnelContextArea,
  FunnelContextTypes,
} from "./types";
import { useFunnel } from "./hooks/useFunnel";
import { memo, useMemo, useRef } from "react";
import { useContentHeightMeasurement } from "../../hooks/useContentHeightMeasurement";
import { useIsSmallBreakpoint } from "../../utils/useBreakpoints";
import { Z_INDEX_NAVBAR } from "../../screens/root/constants";
import { useOverscrollClose } from "../payments/hooks/useOverscrollClose";

export function setFunnelModalConfig(config?: FunnelConfigTypes) {
  // Use a microtask to batch updates and avoid duplicate executions
  queueMicrotask(() => {
    if (!config) {
      rootComponentsStore.funnelModal.set({});
      return;
    }
    rootComponentsStore.funnelModal.set(config);
  });
}

// Lightweight wrapper that only subscribes to `type` to decide whether to render
export function FunnelModal() {
  const type = useHookstate(rootComponentsStore.funnelModal.type).get({
    noproxy: true,
  });

  // Early bailout - don't render anything if no funnel is open
  if (!type) {
    return null;
  }

  return <FunnelModalContent />;
}

// The actual modal content - only renders when a funnel is open
const FunnelModalContent = memo(function FunnelModalContent() {
  const onClose = () => {
    setFunnelModalConfig();
  };

  const funnelModal = rootComponentsStore.funnelModal;
  const type = useHookstate(funnelModal.type).get({ noproxy: true });
  const options = useHookstate(funnelModal.options).get({ noproxy: true });
  const context = useHookstate(funnelModal.context).get({
    noproxy: true,
  }) as FunnelContextTypes;

  const contentRef = useRef<HTMLDivElement>(null);
  const isSmallBreakpoint = useIsSmallBreakpoint();

  const modalOptions = useMemo(() => {
    return {
      ...options,
      isModal: true,
    };
  }, [options]);

  const {
    onClose: onModalClose,
    funnelHeader,
    funnelBody,
    funnelFooter,
  } = useFunnel({
    type,
    context,
    options: modalOptions,
    onClose,
    setFunnelConfig: setFunnelModalConfig,
  });

  const isVerticalReader =
    context?.modalContext === FunnelContextArea.verticalReader;

  useOverscrollClose({
    enabled: isSmallBreakpoint && isVerticalReader,
    onClose: onModalClose || onClose,
  });

  // Only measure on desktop - mobile uses fixed full-screen height
  const contentHeight = useContentHeightMeasurement({
    contentRef,
    dependencies: [type],
    enabled: !!type && !isSmallBreakpoint,
  });

  const isPaymentModal = type
    ? [
        FUNNEL_TYPES.subscription,
        FUNNEL_TYPES.purchaseRelease,
        FUNNEL_TYPES.purchaseBundle,
        FUNNEL_TYPES.prePaywall,
        FUNNEL_TYPES.changeUsername,
      ].includes(type)
    : false;

  const isStartNextReleaseContext =
    context?.modalContext === FunnelContextArea.startNextReleaseButton;

  // Calculate dynamic dimensions based on funnel type
  const dynamicWidth =
    type === FUNNEL_TYPES.onboarding
      ? { base: "900px", ["2xl"]: "1200px" }
      : "558px";
  const dynamicHeight = contentHeight ? `${contentHeight}px` : "auto";

  return (
    <GCModal
      isOpen={!!type}
      maxWidth={dynamicWidth}
      minWidth="558px"
      maxHeight="90vh"
      height={dynamicHeight}
      minHeight={undefined}
      onClose={onModalClose || (() => {})}
      header={funnelHeader}
      body={funnelBody}
      footer={funnelFooter}
      locked={options?.locked}
      trapFocus={options?.trapFocus}
      dividerPadding={type === FUNNEL_TYPES.pdf ? "24px" : undefined}
      hideDivider={isPaymentModal || isStartNextReleaseContext}
      enableSizeTransitions={true}
      contentRef={contentRef}
      zIndex={Z_INDEX_NAVBAR + 1}
    />
  );
});
